import { formatGameARamaText } from '../../utils/auctionLabels';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bell, Check, Clock, Info, AlertCircle,
    Heart, MessageSquare, Star, Mail, CreditCard, ChevronRight,
    UserPlus, ShieldCheck, ShieldX, TrendingUp, Video, Coins, Trophy, Gavel,
    Repeat, AtSign, Smile, PlusCircle, MinusCircle, Package, Layout, MapPin, FileUp, CalendarCheck, Wallet, CheckCircle, XCircle, Zap
} from 'lucide-react';
import { formatTimeAgo } from '../../utils/dateUtils';
import api from '../../services/api';
import { useAppSelector } from '../../hooks/redux';
import { useNavigate } from 'react-router-dom';
import {
    auctionPath,
    conversationPath,
    postPath,
    privateSessionPath,
    profilePath,
    streamFullscreenPath,
    ticketPath,
    otcTradePath,
} from '../../utils/publicIds';

interface Notification {
    id: number;
    title: string;
    message: string;
    type: string;
    data: any;
    is_read: boolean;
    created_at: string;
}

const NOTIFICATION_POLL_TIMEOUT_MS = 8_000;
const NOTIFICATION_FALLBACK_POLL_MS = 120_000;
const NOTIFICATION_MIN_REFRESH_MS = 15_000;

const NotificationBell: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);
    const lastFetchAtRef = useRef(0);
    const hasLoadedListRef = useRef(false);
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();

    const mergeNotifications = useCallback((incoming: Notification[]) => {
        if (!incoming.length) return;

        setNotifications((prev) => {
            const byId = new Map(prev.map((notification) => [notification.id, notification]));
            let unreadDelta = 0;

            incoming.forEach((notification) => {
                const existing = byId.get(notification.id);
                if (!existing && !notification.is_read) {
                    unreadDelta += 1;
                }
                byId.set(notification.id, { ...existing, ...notification });
            });

            if (unreadDelta > 0) {
                setUnreadCount((count) => count + unreadDelta);
            }

            return Array.from(byId.values())
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .slice(0, 50);
        });
    }, []);

    const fetchNotifications = useCallback(async (includeList = false, force = false) => {
        if (!isAuthenticated) return;
        if (isFetchingRef.current) return;
        if (!includeList && !force && Date.now() - lastFetchAtRef.current < NOTIFICATION_MIN_REFRESH_MS) return;

        isFetchingRef.current = true;
        try {
            const countResponse = await api.get('/notifications/unread-count', {
                params: { _t: Date.now() },
                timeout: NOTIFICATION_POLL_TIMEOUT_MS,
            });
            setUnreadCount(countResponse.data.count);

            if (includeList) {
                const response = await api.get('/notifications/', {
                    params: { _t: Date.now() },
                    timeout: NOTIFICATION_POLL_TIMEOUT_MS,
                });
                setNotifications(response.data);
                hasLoadedListRef.current = true;
            }
            lastFetchAtRef.current = Date.now();
        } catch (error) {
            console.warn('Notification refresh skipped:', error);
        } finally {
            isFetchingRef.current = false;
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;

        fetchNotifications(false, true);
        // SSE is the primary delivery path. This is a quiet safety net for missed reconnects.
        const interval = setInterval(() => fetchNotifications(false), NOTIFICATION_FALLBACK_POLL_MS);
        return () => clearInterval(interval);
    }, [isAuthenticated, fetchNotifications]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const handleRealtimeNotifications = (event: Event) => {
            const notifications = (event as CustomEvent<Notification[]>).detail;
            if (Array.isArray(notifications)) {
                mergeNotifications(notifications);
            }
        };

        const handleVisible = () => {
            if (document.visibilityState === 'visible') {
                fetchNotifications(false);
            }
        };

        const handleFocus = () => fetchNotifications(false);

        window.addEventListener('issac:notifications', handleRealtimeNotifications as EventListener);
        document.addEventListener('visibilitychange', handleVisible);
        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('issac:notifications', handleRealtimeNotifications as EventListener);
            document.removeEventListener('visibilitychange', handleVisible);
            window.removeEventListener('focus', handleFocus);
        };
    }, [isAuthenticated, fetchNotifications, mergeNotifications]);

    // ... marksAsRead logic ...

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        if (nextOpen && !hasLoadedListRef.current) {
            fetchNotifications(true);
        }
    };

    const markAsRead = async (id: number) => {
        try {
            await api.patch(`/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleNotificationClick = (notification: Notification) => {

        if (!notification.is_read) {
            markAsRead(notification.id);
        }
        setIsOpen(false);

        const data = notification.data || {};
        const type = notification.type;

        // Navigation logic based on type and data
        if (type === 'new_follower' || type === 'user_followed') {
            const username = data.followerUsername || data.follower_username;
            if (username) {
                navigate(profilePath(username));
            } else {
                const fId = data.followerId || data.follower_id;
                if (fId) navigate(profilePath(fId));
            }
        } else if (type === 'stream_live' || type === 'creator_live' || type === 'go_live') {
            const streamId = data.streamId || data.stream_id || data.sessionId;
            if (streamId) {
                navigate(streamFullscreenPath(streamId));
            }
        } else if (type === 'new_auction' || type === 'auction_ending' || type === 'auction_won' || type === 'auction_win' || type === 'outbid' || type === 'new_bid') {
            const auctionId = data.auctionId || data.auction_id;
            if (auctionId) {
                navigate(auctionPath(auctionId));
            } else if (type === 'auction_win' || type === 'auction_won') {
                navigate('/game-a-rama/my-wins');
            }
        } else if (type === 'new_post') {
            const postId = data.postId || data.post_id;
            if (postId) navigate(postPath(postId));
            else navigate('/feed');
        } else if (type === 'direct_message' || data.conversationId) {
            navigate(conversationPath(data.conversationId || ''));
        } else if (type === 'item_shipped' || type === 'delivery_initiated' || type === 'code_generated') {
            navigate('/deliveries');
        } else if (type === 'tokens_purchased' || type === 'tokens_credited' || type === 'tokens_debited') {
            navigate('/wallet');
        } else if (type === 'new_subscriber') {
            navigate('/subscriptions/subscribers');
        } else if (type === 'go_live_request') {
            navigate('/go-live');
        } else if (type.startsWith('ad_campaign_') || type.startsWith('campaign_')) {
            navigate('/ads');
        } else if (type === 'ad_wallet_deposit') {
            navigate('/ads/wallet');
        } else if (type === 'otc_trade' || data.tradeId) {
            navigate(otcTradePath(data.tradeId || ''));
        } else if (type === 'date_reminder') {
            const requestId = data.requestId || data.request_id || data.sessionId;
            if (requestId) navigate(privateSessionPath(requestId));
        } else if (data.ticketId) {
            navigate(ticketPath(data.ticketId));
        }
    };


    const markAllAsRead = async () => {
        try {
            await api.post('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'auction_win':
            case 'auction_won':
            case 'game_win':
                return <Trophy className="w-4 h-4 text-amber-500" />;
            case 'outbid': return <AlertCircle className="w-4 h-4 text-red-500" />;
            case 'item_shipped':
            case 'delivery_initiated':
                return <Package className="w-4 h-4 text-blue-500" />;
            case 'payout_approved': return <CreditCard className="w-4 h-4 text-green-500" />;
            case 'payout_rejected': return <ShieldX className="w-4 h-4 text-red-500" />;
            case 'post_like': return <Heart className="w-4 h-4 text-pink-500" />;
            case 'post_comment': return <MessageSquare className="w-4 h-4 text-indigo-500" />;
            case 'post_share': return <Repeat className="w-4 h-4 text-emerald-500" />;
            case 'mention': return <AtSign className="w-4 h-4 text-blue-400" />;
            case 'message_reaction': return <Smile className="w-4 h-4 text-amber-400" />;
            case 'direct_message': return <MessageSquare className="w-4 h-4 text-emerald-500" />;
            case 'new_subscriber': return <Star className="w-4 h-4 text-purple-500" />;
            case 'support_message': return <Mail className="w-4 h-4 text-orange-500" />;
            case 'new_bid': return <Gavel className="w-4 h-4 text-cyan-500" />;
            case 'user_followed':
            case 'new_follower':
                return <UserPlus className="w-4 h-4 text-blue-400" />;
            case 'creator_application_approved': return <ShieldCheck className="w-4 h-4 text-green-500" />;
            case 'creator_application_rejected': return <ShieldX className="w-4 h-4 text-red-500" />;
            case 'campaign_approved':
            case 'ad_campaign_approved':
                return <CheckCircle className="w-4 h-4 text-green-400" />;
            case 'campaign_rejected':
            case 'ad_campaign_rejected':
                return <XCircle className="w-4 h-4 text-red-400" />;
            case 'campaign_completed':
            case 'ad_campaign_completed':
                return <Zap className="w-4 h-4 text-primary-500" />;
            case 'nav_otc':
            case 'otc_trade':
                return <Repeat className="w-4 h-4 text-indigo-500" />;
            case 'new_earning': return <TrendingUp className="w-4 h-4 text-emerald-500" />;
            case 'new_tip': return <Coins className="w-4 h-4 text-amber-400" />;
            case 'stream_live':
            case 'creator_live':
            case 'go_live_request':
                return <Video className="w-4 h-4 text-rose-500" />;
            case 'stream_scheduled':
            case 'auction_ending':
            case 'date_reminder':
                return <Clock className="w-4 h-4 text-indigo-400" />;
            case 'tokens_purchased':
            case 'tokens_credited':
                return <PlusCircle className="w-4 h-4 text-emerald-500" />;
            case 'tokens_debited': return <MinusCircle className="w-4 h-4 text-rose-500" />;
            case 'new_post': return <Layout className="w-4 h-4 text-blue-400" />;
            case 'address_submitted': return <MapPin className="w-4 h-4 text-indigo-500" />;
            case 'file_uploaded': return <FileUp className="w-4 h-4 text-primary-500" />;
            case 'date_completed': return <CalendarCheck className="w-4 h-4 text-emerald-500" />;
            case 'ad_wallet_deposit': return <Wallet className="w-4 h-4 text-amber-500" />;
            case 'code_generated': return <Zap className="w-4 h-4 text-teal-400" />;
            default: return <Info className="w-4 h-4 text-emerald-500" />;
        }
    };

    if (!isAuthenticated) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleToggle}
                className="p-2 sm:p-2.5 rounded-lg sm:rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] transition-all relative"
                aria-label="Notifications"
            >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-[#0a0e27] animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 sm:mt-3 sm:w-[380px] bg-white dark:bg-[#0d1333] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e2a5e] overflow-hidden z-[100] top-[70px] sm:top-auto"
                    >
                        <div className="p-4 border-b border-gray-100 dark:border-[#1e2a5e] flex items-center justify-between bg-gradient-to-r from-emerald-500/5 to-teal-500/5">
                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <Bell className="w-4 h-4 text-emerald-500" />
                                Notifications
                            </h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs font-semibold text-emerald-500 hover:text-emerald-600 transition-colors flex items-center gap-1"
                                >
                                    <Check className="w-3 h-3" />
                                    Mark all read
                                </button>
                            )}
                        </div>

                        <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                            {notifications.length === 0 ? (
                                <div className="p-10 text-center">
                                    <div className="w-12 h-12 bg-gray-100 dark:bg-[#131a45] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Bell className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">No notifications yet</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-50 dark:divide-[#1e2a5e]">
                                    {notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`p-4 flex gap-3 hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all cursor-pointer relative group ${!notification.is_read ? 'bg-emerald-50/30 dark:bg-emerald-500/5' : ''}`}
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${!notification.is_read ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-gray-100 dark:bg-[#131a45]'}`}>
                                                {getIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <p className={`text-sm font-bold truncate ${!notification.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                                                        {formatGameARamaText(notification.title)}
                                                    </p>
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {formatTimeAgo(notification.created_at)}
                                                    </span>
                                                </div>
                                                <p className={`text-xs leading-relaxed line-clamp-2 ${!notification.is_read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`}>
                                                    {formatGameARamaText(notification.message)}
                                                </p>
                                            </div>
                                            {!notification.is_read && (
                                                <div className="absolute top-4 right-4 w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-3 bg-gray-50 dark:bg-[#0a0e27] border-t border-gray-100 dark:border-[#1e2a5e] text-center">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/notifications');
                                }}
                                className="text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-emerald-500 transition-colors flex items-center justify-center gap-1 mx-auto"
                            >
                                View all activity
                                <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationBell;
